import React, { useState, useRef } from 'react';
import {
  Box,
  Textarea,
  Button,
  Stack,
  Typography,
} from '@mui/joy';
import { PaperPlaneTilt as SendIcon } from '@phosphor-icons/react';
import { MentionDropdown } from './MentionDropdown';
import type { User } from '../../types';

interface CommentInputProps {
  onSubmit: (content: string) => void;
  users: User[];
  isSubmitting?: boolean;
  placeholder?: string;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  onSubmit,
  users,
  isSubmitting = false,
  placeholder = 'Add a comment... Use @ to mention someone',
}) => {
  const [content, setContent] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setContent(value);
    setCursorPosition(cursorPos);

    // Check for @ symbol
    const beforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const afterAt = beforeCursor.substring(lastAtIndex + 1);
      // Only show dropdown if @ is not followed by a space and we're still typing after it
      if (!afterAt.includes(' ') && cursorPos > lastAtIndex) {
        setMentionSearch(afterAt);
        setShowMentionDropdown(true);
        updateDropdownPosition();
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // Update dropdown position based on textarea position and cursor location
  const updateDropdownPosition = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      
      // Create a temporary element to measure text dimensions
      const mirror = document.createElement('div');
      const computed = window.getComputedStyle(textarea);
      
      // Copy textarea styles to mirror element
      mirror.style.position = 'absolute';
      mirror.style.visibility = 'hidden';
      mirror.style.whiteSpace = 'pre-wrap';
      mirror.style.wordWrap = 'break-word';
      mirror.style.width = textarea.offsetWidth + 'px';
      mirror.style.fontSize = computed.fontSize;
      mirror.style.fontFamily = computed.fontFamily;
      mirror.style.lineHeight = computed.lineHeight;
      mirror.style.padding = computed.padding;
      
      // Get text up to cursor position
      const textBeforeCursor = content.substring(0, cursorPosition);
      mirror.textContent = textBeforeCursor;
      
      document.body.appendChild(mirror);
      
      // Calculate position based on the mirror element
      const mirrorHeight = mirror.offsetHeight;
      
      // Clean up
      document.body.removeChild(mirror);
      
      // Position dropdown relative to the textarea
      // Add some offset to position it nicely below the current line
      const topPosition = Math.min(mirrorHeight + 30, textarea.offsetHeight + 20);
      
      setDropdownPosition({
        top: topPosition,
        left: 10,
      });
    }
  };

  // Handle user selection from dropdown
  const handleUserSelect = (user: User) => {
    const beforeCursor = content.substring(0, cursorPosition);
    const afterCursor = content.substring(cursorPosition);
    const lastAtIndex = beforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const beforeAt = content.substring(0, lastAtIndex);
      const newContent = `${beforeAt}@${user.username} ${afterCursor}`;
      setContent(newContent);
      
      // Set cursor position after the username
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = lastAtIndex + user.username.length + 2; // +2 for @ and space
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    }
    
    setShowMentionDropdown(false);
    setMentionSearch('');
  };

  // Handle submit
  const handleSubmit = () => {
    if (content.trim() && !isSubmitting) {
      onSubmit(content.trim());
      setContent('');
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Don't handle if mention dropdown is open
    if (showMentionDropdown) {
      if (e.key === 'Escape') {
        setShowMentionDropdown(false);
        e.preventDefault();
      }
      return;
    }

    // Ctrl/Cmd + Enter to submit
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack spacing={1}>
        <Textarea
          value={content}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          minRows={3}
          maxRows={8}
          disabled={isSubmitting}
          sx={{
            '&:focus': {
              borderColor: 'primary.500',
            },
          }}
        />
        
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography level="body-xs" color="neutral">
            {content.length > 0 && `${content.length} characters`}
            {content.length === 0 && 'Press Ctrl+Enter to submit'}
          </Typography>
          
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            loading={isSubmitting}
            endDecorator={<SendIcon />}
          >
            Comment
          </Button>
        </Stack>
      </Stack>

      {showMentionDropdown && (
        <MentionDropdown
          users={users}
          searchTerm={mentionSearch}
          onSelect={handleUserSelect}
          onClose={() => setShowMentionDropdown(false)}
          position={dropdownPosition}
        />
      )}
    </Box>
  );
};